import { Injectable } from '@angular/core';
import { CustomerWithOffice, GetAllCustomersRequest } from '@dave/types/dist/proto/baseService/customer';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, from, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { filter, map, skip } from 'rxjs/operators';
import {
    SelectSearchOption,
} from 'src/app/dave-utils-module/select-search/components/select-search/select-search.component';
import { Address, getAddressString, isNotNullOrUndefined } from '../../helper/helper';
import { CustomerEntity, CustomerEntityFromGRPCBackend } from '../entities/customer.entity';
import { State } from '../State';
import { AppGatewayService } from './app-gateway.service';
import { HttpService } from './http.service';
import { PageEvent } from '@angular/material/paginator';
import { customerBackendFilter, customerBackendSort } from './paginated-customer.service';
import { DetectionResultDataInvoice } from '@dave/types';
import { FilterTypes } from '../../services/default-filter.service';
import {
    FILTER_TYPE_BOOLEAN,
    FILTER_TYPE_SEARCH_MULTI_SELECT,
    FilterOption,
    IFilterTypeSearchMultiSelectValue,
} from '../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { getOwnPartnerOffices } from '../State/selectors/partners.selectors';
import { getFetched$ } from '../helper/helper';
import {
    getCustomerSpecificationTypes,
    getCustomerSpecificationTypesFetched,
} from '../State/selectors/customerSpecificationType.selectors';
import { CustomLabelService } from '../../services/custom-label/custom-label.service';


export const CustomerSearchPageSize = 50;
const DefaultUnnamedCustomerLabel = 'unbenannter Kunde';

/**
 * Baut das Label für die Kunden-Auswahl: "<Kundennr.> <Name> - <Adresse>".
 * Die Adresse wird – wie bisher – nur angehängt, wenn eine Kundennummer existiert.
 */
export function getCustomerOptionLabel(customer: CustomerEntity, address?: Address, unnamedLabel = DefaultUnnamedCustomerLabel): string {
    if (!isNotNullOrUndefined(customer.CustomerNo)) {
        return isNotNullOrUndefined(customer.Name) ? customer.Name : unnamedLabel;
    }
    const addressString = address ? getAddressString(address, false) : '';
    return [customer.CustomerNo, customer.Name, addressString && '- ' + addressString].filter((part) => !!part).join(' ');
}

/**
 * CustomerEntity.optionLabel ist ein Getter ohne Setter, eine Zuweisung würde im strict mode
 * knallen. Deshalb wird das abweichende Label per defineProperty auf die Instanz gelegt und
 * verdeckt dort den Prototyp-Getter – der Prototyp (DisplayName, Clone, …) bleibt erhalten.
 */
export function withOptionLabel(customer: CustomerEntity, optionLabel: string): SelectSearchOption<CustomerEntity> {
    return Object.defineProperty(customer, 'optionLabel', {
        value: optionLabel,
        enumerable: true,
        configurable: true,
        writable: true,
    });
}

@Injectable({
    providedIn: 'root',
})
export class CustomerDataService {
    constructor(
        private store: Store<State>,
        private gatewayHttpService: HttpService,
        private gatewayService: AppGatewayService,
        private cls: CustomLabelService,
    ) {
    }

    public async findCustomersFromDocumentDetection(
        doc: DetectionResultDataInvoice,
    ): Promise<CustomerEntity[]> {
        const requests: Promise<CustomerEntity[]>[] = [];

        if (doc.FromName?.length > 2) {
            requests.push(
                this.fetchCustomersPage(doc.FromName, CustomerSearchPageSize)
                    .then(res => res.customers),
            );
        }

        if (doc.CustomerNo?.length > 2) {
            requests.push(
                this.fetchCustomersPage(doc.CustomerNo, CustomerSearchPageSize)
                    .then(res => res.customers),
            );
        }

        if (requests.length === 0) {
            return [];
        }

        const results = await Promise.all(requests);

        return results.flat();
    }

    public async fetchCustomerSearchPage(
        searchQuery: string,
        lastId: number | undefined,
        unnamedLabel: string,
        filter?: customerBackendFilter,
    ): Promise<{
        options: SelectSearchOption<CustomerEntity>[];
        nextLastId?: number;
        pageCount?: number;
    }> {
        const res = await this.gatewayService.Request({
            BaseCustomerGet: {
                CustomerIds: [],
                SearchQuery: searchQuery,
                PageSize: CustomerSearchPageSize.toString(),
                LastId: lastId?.toString(),
                PartnerOfficeIds: filter?.partnerOfficeIds?.map((id) => id.toString()) ?? [],
                CustomerSpecificationTypeIds: filter?.customerSpecificationTypeIds?.map((id) => id.toString()) ?? [],
                OnlyCustomers: filter?.onlyCustomers,
                OnlySuppliers: filter?.onlySuppliers,
            },
        });

        const nextLastId = res.BaseCustomerGet.LastId ? Number(res.BaseCustomerGet.LastId) : undefined;
        const pageCount = res.BaseCustomerGet.PageCount != null ? Number(res.BaseCustomerGet.PageCount) : undefined;


        const cache = new Map(this.cache.value);
        const options = res.BaseCustomerGet.CustomerWithOffice.map((c) => {
            const customer = CustomerEntityFromGRPCBackend(c);
            cache.set(customer.Id, { entity: customer, lastFetched: new Date() });
            if (!this.fetchedCustomersIds.includes(customer.Id)) {
                this.fetchedCustomersIds.push(customer.Id);
            }
            const office = c.Offices?.[0];
            const address: Address | undefined = office && {
                Street: office.Street,
                PostalCode: office.PostalCode,
                City: office.City,
                Country: office.Country,
            };
            return withOptionLabel(customer, getCustomerOptionLabel(customer, address, unnamedLabel));
        });
        this.cache.next(cache);

        return { options, nextLastId, pageCount };
    }

    public createSearchState(defaultFilter?: customerBackendFilter): CustomerSearchState {
        return new CustomerSearchState(this, defaultFilter);
    }






    public async fetchCustomersPage(
        searchQuery: string,
        pageSize: number,
        lastId?: number,
        filter?: customerBackendFilter,
        sort?: customerBackendSort,
        streetNotNull?: boolean
    ): Promise<{
        customers: CustomerEntity[];
        rawCustomerWithOffice: CustomerWithOffice[];
        nextLastId?: number;
        pageCount?: number;
    }> {
        const res = await this.gatewayService.Request({
            BaseCustomerGet: {
                CustomerIds: [],
                SearchQuery: searchQuery,
                PageSize: pageSize.toString(),
                LastId: lastId?.toString(),
                StreetNotNull: streetNotNull ?? false,
                PartnerOfficeIds: filter?.partnerOfficeIds?.map((id) => id.toString()) ?? [],
                CustomerSpecificationTypeIds: filter?.customerSpecificationTypeIds?.map((id) => id.toString()) ?? [],
                OnlyCustomers: filter?.onlyCustomers,
                OnlySuppliers: filter?.onlySuppliers,
                SortPropertyWrapper: sort ? { SortProperty: sort.property } : undefined,
                SortDirectionWrapper: sort ? { SortDirection: sort.direction } : undefined,
            },
        });

        const nextLastId = res.BaseCustomerGet.LastId ? Number(res.BaseCustomerGet.LastId) : undefined;
        const pageCount = res.BaseCustomerGet.PageCount != null ? Number(res.BaseCustomerGet.PageCount) : undefined;

        // Zentraler Cache wird trotzdem gefüllt/aktualisiert, egal wer die Seite angefordert hat
        const cache = new Map(this.cache.value);
        const customers = res.BaseCustomerGet.CustomerWithOffice.map((c) => {
            const customer = CustomerEntityFromGRPCBackend(c);
            cache.set(customer.Id, { entity: customer, lastFetched: new Date() });
            if (!this.fetchedCustomersIds.includes(customer.Id)) {
                this.fetchedCustomersIds.push(customer.Id);
            }
            return customer;
        });
        this.cache.next(cache);

        return { customers, rawCustomerWithOffice: res.BaseCustomerGet.CustomerWithOffice, nextLastId, pageCount };
    }



    private pendingCustomerRequests = new Map<number, Promise<CustomerEntity[]>>();


    protected cache = new BehaviorSubject<Map<number, {
        entity: CustomerEntity
        lastFetched: Date
    }>>(new Map());
    protected fetchedCustomersIds: number[] = [];

    protected filterDuplicates(ids: number[]): number[] {
        return [...new Set(ids)];
    }

    ConvertDict(input: CustomerEntity[]): Dictionary<CustomerEntity> {
        let out: Dictionary<CustomerEntity> = {};
        input.forEach(e => out[e.Id] = e);
        return out;
    }

    // todo warum ist waitForAll defaultmäßig false ?
    GetCustomers$(idsIn: number[], waitForAll: boolean = false, withDeleted: boolean = false): Observable<CustomerEntity[]> {
        if (idsIn.length === 0) {
            return of([]);
        }
        let ids = this.filterDuplicates(idsIn).filter(id => !!id);
        if (ids.length === 0) {
            return of([]);
        }
        let hasMissing = false;
        for (const id of ids) {
            if (!this.fetchedCustomersIds.includes(id)) {
                hasMissing = true;
                break;
            }
        }
        if (waitForAll) {
            return from(this.GetCustomers(ids, withDeleted)).pipe(
                switchMap(() => this.cache),
                map(customers => ids.map(id => customers.get(id)?.entity).filter(isNotNullOrUndefined)),
                filter(isNotNullOrUndefined),
            )
        }
        this.GetCustomers(ids, withDeleted);
        return this.cache.pipe(
            // todo man kann natürlich nicht auf cache warten und einfach einen skippen, da die cache variable nicht nur in dieser funktion genutzt wird
            skip((hasMissing || waitForAll) ? 1 : 0),
            map(customers => ids.map(id => customers.get(id)?.entity).filter(isNotNullOrUndefined)),
            filter(isNotNullOrUndefined),
        );

    }

    GetCustomersDict$(ids: number[], waitForAll: boolean = false): Observable<Dictionary<CustomerEntity>> {
       return this.GetCustomers$(ids, waitForAll, true).pipe(map(res => this.ConvertDict(res)))
    }

    GetSingleCustomer$(id: number): Observable<CustomerEntity> {
        this.GetCustomers([id], true);
        return this.cache.pipe(
            map(e => e.get(id)),
            filter(isNotNullOrUndefined),
            map(e => e.entity),
        );
    }

    GetCustomers(idsIn: number[], withDeleted = false): Promise<CustomerEntity[]> {
        let ids = this.filterDuplicates(idsIn);
        let missingIds = ids.filter(id => id !== null).filter(id => !this.fetchedCustomersIds.includes(id));
        const pendingIds = missingIds.filter(id => this.pendingCustomerRequests.has(id));
        if (pendingIds.length === missingIds.length && pendingIds.length > 0) {
            return Promise.all(
                pendingIds.map(id => this.pendingCustomerRequests.get(id)!)
            ).then(results => results.flat());
        }
        let updateIds = ids.filter(id => this.fetchedCustomersIds.includes(id));
        let missing: CustomerEntity[] = [];
        let updated: CustomerEntity[] = [];
        let missingFetched = false;
        let updateFetched = false;

        return new Promise((resolve, reject) => {
            let resPromise = () => {
                if ((missingIds.length == 0 || missingFetched) && (updateIds.length == 0 || updateFetched)) {
                    resolve([...missing, ...updated]);
                }
            };

            if (missingIds.length != 0) {

                const request = this.gatewayService.Request({
                    BaseCustomerGet: {
                        // todo warum werden nur die fehlenden abgerufen ?
                        CustomerIds: missingIds.map(id => id.toString()),
                        PartnerOfficeIds: [],
                        CustomerSpecificationTypeIds: [],
                        WithDeleted: withDeleted,
                    },
                }).then(res => {

                    missing = res.BaseCustomerGet.CustomerWithOffice
                        .map(e => CustomerEntityFromGRPCBackend(e));

                    const nv = new Map(this.cache.value);

                    missing.forEach(e => {
                        nv.set(e.Id, {
                            entity: e,
                            lastFetched: new Date()
                        });
                    });

                    this.cache.next(nv);

                    this.fetchedCustomersIds.push(
                        ...missing.map(e => e.Id)
                    );

                    missingFetched = true;
                    resPromise();

                    return missing;

                }).catch(err => {
                    reject(err);
                    throw err;

                }).finally(() => {
                    missingIds.forEach(id => {
                        this.pendingCustomerRequests.delete(id);
                    });

                });


                missingIds.forEach(id => {
                    this.pendingCustomerRequests.set(id, request);
                });
            }

            if (updateIds.length != 0){
                let maxDate = new Date(2000,0,0).getTime();
                this.cache.value.forEach((value, key) => {
                    if (updateIds.includes(key)){
                        let check = value.lastFetched.getTime();
                        if (check > maxDate){
                            maxDate = check
                        }
                    }
                })
                this.gatewayService.Request({
                    BaseCustomerGet: {
                        CustomerIds: updateIds.map(id => id.toString()),
                        UpdatedSince: maxDate.toString(),
                        PartnerOfficeIds: [],
                        CustomerSpecificationTypeIds: [],
                    },
                }).then(res => {
                    updated = res.BaseCustomerGet.CustomerWithOffice.map(e => CustomerEntityFromGRPCBackend(e))
                    let nv = this.cache.value;
                    updated.forEach(e => {
                        nv.set(e.Id, {
                            entity: e,
                            lastFetched: new Date()
                        });
                    })
                    this.cache.next(nv);
                    this.fetchedCustomersIds.push(...updated.map(e => e.Id));
                    updateFetched = true;
                    resPromise();
                }).catch(err => {
                    reject(err);
                });
            }
        });
    }
    getAll$(payload: GetAllCustomersRequest): Observable<CustomerEntity[]> {
        return this.gatewayService.Request$({ BaseCustomerGetAll: payload }).pipe(
            map((response) => {
                if (Object.keys(response?.Errors || {}).length === 0) {
                    return response.BaseCustomerGetAll.CustomerWithOffice.map(c => CustomerEntityFromGRPCBackend(c));
                } else {
                    throw response.Errors;
                }
            }),
        );
    }
    defaultFilterSettings$: Observable<FilterOption[]> = combineLatest([
        this.store.select(getOwnPartnerOffices).pipe(filter(isNotNullOrUndefined)),
        getFetched$(this.store, getCustomerSpecificationTypesFetched, getCustomerSpecificationTypes),
        this.cls.getMultiple$('CustomerSpecificationType'),
        this.cls.getMultiple$('Customer'),
        this.cls.getMultiple$('Supplier'),
    ]).pipe(
        map(([partners, customerTypes, customerTypeLabel, customersRoleLabel, suppliersLabel]) => {
            const ret: FilterOption[] = [];
            if (partners.length) {
                const partnerValues = partners.map((partner) => ({
                    label: partner.DisplayName,
                    id: partner.Id,
                }));
                ret.push({
                    Name: FilterTypes.PartnerOfficeId,
                    Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                    Label: 'Unternehmen',
                    Icon: 'building',
                    Values: partnerValues,
                });
            }
            const customerTypeOptions = customerTypes.map((c) => ({
                label: c.Name,
                id: c.Id,
            }));
            ret.push({
                Name: FilterTypes.CustomerSpecificationTypeId,
                Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                Label: customerTypeLabel,
                Values: customerTypeOptions,
            });
            ret.push(
                {
                    Name: FilterTypes.Customers,
                    Type: FILTER_TYPE_BOOLEAN,
                    Label: customersRoleLabel,
                },
                {
                    Name: FilterTypes.Supplier,
                    Type: FILTER_TYPE_BOOLEAN,
                    Label: suppliersLabel,
                },
            );
            return ret;
        }),
    );
}
export interface CustomerListFilterValues {
    [FilterTypes.PartnerOfficeId]: IFilterTypeSearchMultiSelectValue<number>[];
    [FilterTypes.Customers]: boolean;
    [FilterTypes.Supplier]: boolean;
    [FilterTypes.CustomerSpecificationTypeId]: IFilterTypeSearchMultiSelectValue<number>[];
}
export class CustomerSearchState {
    public customerOptions$ = new BehaviorSubject<SelectSearchOption<CustomerEntity>[]>([]);
    public loading$ = new BehaviorSubject(false);
    public customerPageIndex$ = new BehaviorSubject(0);
    public customerPageCount$ = new BehaviorSubject(0);

    public readonly customerPaging = {
        pageIndex$: this.customerPageIndex$,
        pageCount$: this.customerPageCount$,
        pageChange: (event: PageEvent) => this.customerPageChange(event),
    };

    private currentSearch = '';
    public currentUnnamedLabel = DefaultUnnamedCustomerLabel;
    private pageLastIds: (number | undefined)[] = [undefined];
    private currentPageIndex = 0;
    private _currentFilter: CustomerListFilterValues;
    public get currentFilter() {
        return {... this._currentFilter};
    }

    private _filterValues$: ReplaySubject<CustomerListFilterValues> = new ReplaySubject<CustomerListFilterValues>(1);
    public filterValues$= this._filterValues$.asObservable();
    public filterValuesCount$= this.filterValues$.pipe(map(f => {
        let count = 0;
        Object.keys(f).forEach((key: keyof CustomerListFilterValues) => {
            switch (key) {
                case FilterTypes.PartnerOfficeId:
                case FilterTypes.CustomerSpecificationTypeId:
                    if (f[key]?.length) {
                        count++;
                    }
                    break;
                case FilterTypes.Supplier:
                case FilterTypes.Customers:
                    if (f[key]) {
                        count++;
                    }
                    break;
            }
        });
        if (f[FilterTypes.Supplier] && f[FilterTypes.Customers]) {
            count += -2;
        }
        return count;
    }));
    constructor(public customerData: CustomerDataService, defaultFilter?: customerBackendFilter) {
        this.setFilter({
            [FilterTypes.PartnerOfficeId]: isNotNullOrUndefined(defaultFilter?.[FilterTypes.PartnerOfficeId]) ? defaultFilter[FilterTypes.PartnerOfficeId] : [],
            [FilterTypes.Customers]: isNotNullOrUndefined(defaultFilter?.[FilterTypes.Customers]) ? defaultFilter[FilterTypes.Customers] : false,
            [FilterTypes.Supplier]: isNotNullOrUndefined(defaultFilter?.[FilterTypes.Supplier]) ? defaultFilter[FilterTypes.Supplier] : false,
            [FilterTypes.CustomerSpecificationTypeId]: isNotNullOrUndefined(defaultFilter?.[FilterTypes.CustomerSpecificationTypeId]) ? defaultFilter[FilterTypes.CustomerSpecificationTypeId] : [],
        });
    }

    public setFilter(filter: CustomerListFilterValues) {
        this._currentFilter = filter;
        this._filterValues$.next(filter);
    }
    async FilterCustomers(filter: CustomerListFilterValues) {
        this.setFilter(filter);
        await this.fetchFirstPage();
    }
    async SearchCustomers(search: string, unnamedLabel?: string) {
        this.currentUnnamedLabel = unnamedLabel;
        this.currentSearch = search;
        await this.fetchFirstPage();
    }
    async fetchFirstPage() {
        this.pageLastIds = [undefined];
        this.currentPageIndex = 0;
        this.customerPageIndex$.next(0);
        await this.fetchPage(undefined, this.currentUnnamedLabel, true);
    }

    async customerPageChange(event: PageEvent) {
        const newPageIndex = event.pageIndex;
        if (newPageIndex === this.currentPageIndex) return;
        if (newPageIndex > 0 && this.pageLastIds[newPageIndex] === undefined) return;

        const lastId = this.pageLastIds[newPageIndex];
        this.currentPageIndex = newPageIndex;
        this.customerPageIndex$.next(newPageIndex);
        await this.fetchPage(lastId, DefaultUnnamedCustomerLabel, false);
    }

    private async fetchPage(lastId: number | undefined, unnamedLabel: string, isNewSearch: boolean) {
        this.loading$.next(true);
        try {
            const { options, nextLastId, pageCount } =
                await this.customerData.fetchCustomerSearchPage(this.currentSearch, lastId, unnamedLabel, {
                    partnerOfficeIds: this._currentFilter[FilterTypes.PartnerOfficeId] ? this._currentFilter[FilterTypes.PartnerOfficeId].map(v=>v.id) : [],
                    customerSpecificationTypeIds: this._currentFilter[FilterTypes.CustomerSpecificationTypeId] ? this._currentFilter[FilterTypes.CustomerSpecificationTypeId].map(v=>v.id) : [],
                    onlyCustomers: this._currentFilter[FilterTypes.Customers],
                    onlySuppliers: this._currentFilter[FilterTypes.Supplier],
                });

            if (isNewSearch && pageCount != null) {
                this.customerPageCount$.next(pageCount);
            }
            if (nextLastId) {
                this.pageLastIds[this.currentPageIndex + 1] = nextLastId;
            }

            this.customerOptions$.next(options);
        } finally {
            this.loading$.next(false);
        }
    }
}
